import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, X, Filter } from "lucide-react";
import axios from "axios";
import { useStore } from "@nanostores/react";
import { $products, $filter } from "./products-store";
import { FilterOption, FilterData } from "./types"; // Adjust the import path as necessary

interface FilterState {
  [key: string]: string[];
}

const FilterComponent: React.FC = () => {
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({});
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    Brand: true,
    Type: true
  });
  const [sortBy, setSortBy] = useState("Featured");
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Mock API call to get filter options
  const fetchFilterOptions = async (): Promise<FilterData> => {
    const result = await axios.get("/sfapi/products-legacy/filter"); // Replace with actual API endpoint
    // console.log("Filter options fetched:", result.data);

    const tags = result.data.map(d => d._id) || [];
    const filterOptions = tags.reduce((acc: FilterOption[], tag: string) => {
      const [type, option] = tag.split(":");
      // Only "Type:Option" style tags are filters; skip plain tags like "grandeffects"
      if (!type || !option) return acc;
      if (!acc.some(item => item.type === type)) {
        acc.push({ type, options: [] });
      }
      const filterOption = acc.find(item => item.type === type);
      if (filterOption) {
        filterOption.options.push(option);
      }
      return acc;
    }, []);

    // console.log("Processed filter options:", filterOptions);
    return { filterOptions };

    // Mock data fallback (this code is unreachable but kept for reference)
    /*
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          filterOptions: [
            {
              type: "Brand",
              options: [
                "Polaris",
                "Fluidra",
                "Jandy",
                "Zodiac",
                "Grand Effects",
                "S.R.Smith",
                "CMP",
                "Cover-Pools",
                "Nature2",
                "ProEdge"
              ]
            },
            {
              type: "Type",
              options: [
                "Displays",
                "Legos",
                "Shells",
                "Table Top (for Sales Rep use only)"
              ]
            }
          ]
        });
      }, 500);
    });
    */
  };

  // Mock handler for data reload
  const handleDataReload = (filters: FilterState, sort: string) => {
    console.log("Reloading data with filters:", filters, "and sort:", sort);
    // This would typically make an API call to reload the main data
  };

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const data = await fetchFilterOptions();
        setFilterData(data);
        // Initialize filter store with empty array
        $filter.set([]);

        // Initialize with some pre-selected filters to match the screenshot
        setSelectedFilters({
          // Brand: ["Polaris", "Jandy"],
          // Type: ["Shells"]
        });
      } catch (error) {
        console.error("Failed to load filter options:", error);
      } finally {
        setLoading(false);
      }
    };

    loadFilters();
  }, []);

  const toggleFilterSection = (sectionType: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionType]: !prev[sectionType]
    }));
  };

  const handleFilterChange = (
    filterType: string,
    option: string,
    checked: boolean
  ) => {
    setSelectedFilters(prev => {
      const currentFilters = prev[filterType] || [];
      const newFilters = checked
        ? [...currentFilters, option]
        : currentFilters.filter(item => item !== option);

      const updatedFilters = {
        ...prev,
        [filterType]: newFilters
      };

      // console.log("Updated filters:", updatedFilters);
      // Call the data reload handler
      handleDataReload(updatedFilters, sortBy);

      const tags = Object.entries(updatedFilters).reduce(
        (acc: string[], [filterType, options]) => {
          return acc.concat(options.map(option => `${filterType}:${option}`));
        },
        []
      );
      $filter.set(tags); // Update the nanostore with selected filters
      //
      return updatedFilters;
    });
  };

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  const removeFilter = (filterType: string, option: string) => {
    handleFilterChange(filterType, option, false);
  };

  const clearAllFilters = () => {
    setSelectedFilters({});
    handleDataReload({}, sortBy);
    $filter.set([]); // Clear the nanostore filters
    setIsMobileFilterOpen(false); // Close mobile filter on clear
  };

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
    handleDataReload(selectedFilters, newSort);
  };

  const getAppliedFiltersCount = () => {
    return Object.values(selectedFilters).reduce(
      (acc, val) => acc.concat(val),
      []
    ).length;
  };

  const getTotalItemsCount = () => {
    // Mock total count - in real app this would come from API
    return 48;
  };

  if (loading) {
    return (
      <div className="w-full md:w-64 p-4 bg-white md:border-r border-gray-200">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-4 bg-gray-200 rounded w-20"></div>
            <div className="h-4 bg-gray-200 rounded w-28"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full md:w-64 bg-white md:border-r border-gray-200 h-full flex flex-col">
      {/* Header with results count and sort */}
      {/* <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-600">
            1-15 of {getTotalItemsCount()} items
          </span>
          <button className="text-sm text-blue-600 hover:text-blue-800">
            View All
          </button>
        </div>

        <div className="relative">
          <select
            value={sortBy}
            onChange={e => handleSortChange(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="Featured">Featured</option>
            <option value="Price Low to High">Price Low to High</option>
            <option value="Price High to Low">Price High to Low</option>
            <option value="Name A to Z">Name A to Z</option>
            <option value="Name Z to A">Name Z to A</option>
          </select>
        </div>
      </div>
 */}
      {/* Applied Filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Applied Filters</h3>
          {getAppliedFiltersCount() > 0 && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {Object.entries(selectedFilters).map(([filterType, options]) =>
            options.map(option => (
              <span
                key={`${filterType}-${option}`}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
              >
                {option}
                <button
                  onClick={() => removeFilter(filterType, option)}
                  className="hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))
          )}
        </div>
      </div>

      {/* Filter Sections */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Filter Options</h3>

          {filterData &&
            filterData.filterOptions.map(filterOption => (
              <div key={filterOption.type} className="mb-6">
                <button
                  onClick={() => toggleFilterSection(filterOption.type)}
                  className="flex items-center justify-between w-full py-2 text-left font-medium text-gray-900 hover:text-gray-700"
                >
                  <span>{filterOption.type}</span>
                  {expandedSections[filterOption.type] ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                {expandedSections[filterOption.type] && (
                  <div className="mt-2 space-y-2">
                    {filterOption.options.map(option => (
                      <label
                        key={option}
                        className="flex items-start text-sm text-gray-700 hover:text-gray-900 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={
                            (selectedFilters[filterOption.type] &&
                              selectedFilters[filterOption.type].includes(
                                option
                              )) ||
                            false
                          }
                          onChange={e =>
                            handleFilterChange(
                              filterOption.type,
                              option,
                              e.target.checked
                            )
                          }
                          className="mr-3 mt-0.5 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 flex-shrink-0"
                        />
                        <span className="break-words">{option}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}

          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            Show more options
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterComponent;
